import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  ColumnSort,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ErrorToast } from '~/components/ErrorToast/ErrorToast';
import { STICKY_TABLE_HEADER_CLASSES } from '~/components/ScrollCollapse/constants/classes';
import { cn } from '~/lib/cn';
import { isEnvEnabled } from '~/lib/env';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
} from '~/modules/artist-roster/components/Table';
import { TableHeaderBackground } from '~/modules/artist-roster/components/Table/TableHeaderBackground';
import { TrackTableBody } from '~/modules/artist-roster/components/TrackList/TrackTableBody';
import { getArtistTracksPerformance } from '~/modules/artist-roster/lib/tracks';
import type {
  ArtistDetails,
  AssetFamily,
} from '~/modules/artist-roster/lib/types';

import { useTrackTableColumns } from './useTrackTableColumns';

export interface TrackTableMeta {
  artistName: string;
  filtersCount: number;
  filtersApplied: boolean;
  uaid: number | null;
  partyId: number;
}

interface TrackTableProps {
  data: AssetFamily[];
  meta: TrackTableMeta;
  artistData: ArtistDetails;
  isLoadingTracks: boolean;
  isFetchingTracks: boolean;
  onSortingChange?: OnChangeFn<SortingState>;
  columnSort?: ColumnSort;
}

export function TrackTable({
  data,
  meta,
  artistData,
  isLoadingTracks,
  isFetchingTracks,
  columnSort,
  onSortingChange,
}: TrackTableProps) {
  const { t } = useTranslation('roster');
  const [isErrorToastOpen, setIsErrorToastOpen] = useState(false);
  const familyIds = useMemo(
    () => [...new Set(data?.map((track) => track.id))].sort(),
    [data],
  );
  const [searchParams] = useSearchParams();

  const {
    data: performancesData,
    isLoading: isLoadingPerformance,
    error: performanceError,
  } = useQuery({
    queryKey: ['artist-tracks-performance', familyIds],
    queryFn: () => getArtistTracksPerformance({ familyIds }),
    enabled: !isLoadingTracks && !!familyIds,
    placeholderData: keepPreviousData,
  });

  const trackData = useMemo<AssetFamily[]>(() => {
    if (performanceError) {
      setIsErrorToastOpen(true);
    }

    if (!performancesData) {
      return data;
    }

    return data.map((track) => {
      const performance = performancesData.get(track.id);

      if (!performance) {
        return track;
      }

      return {
        ...track,
        performance,
      };
    });
  }, [data, performancesData, performanceError]);

  const isArtistCollaboratorTracksEnabled = isEnvEnabled(
    'VITE_WMG_ARTIST_COLLABORATOR_TRACKS_ENABLED',
  );

  const isTableViewMetadata =
    isArtistCollaboratorTracksEnabled &&
    searchParams.get('table_view') === 'metadata';

  const columns = useTrackTableColumns({ range: 'TW' });
  const table = useReactTable({
    data: trackData,
    columns,
    meta,
    getCoreRowModel: getCoreRowModel(),
    enableSortingRemoval: false,
    manualSorting: true,
    onSortingChange,
    state: {
      sorting: columnSort ? [columnSort] : undefined,
      columnVisibility: {
        streamsRTD: !isTableViewMetadata,
        streams: !isTableViewMetadata,
        organicStreams: !isTableViewMetadata,
        socialCreations: !isTableViewMetadata,
        contributors: isTableViewMetadata,
      },
    },
  });

  return (
    <>
      <Table aria-label={t('trackList.labels.aria')}>
        <TableHeader className={STICKY_TABLE_HEADER_CLASSES}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'px-sm text-body-sm font-normal first:w-1/3 first:pl-lg',
                      header.column.columnDef.meta?.className,
                    )}
                  >
                    {!index ? <TableHeaderBackground /> : null}
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TrackTableBody
          {...{
            table,
            meta,
            artistData,
            isFetchingTracks,
            isLoadingPerformance,
          }}
        />
      </Table>
      <ErrorToast
        isOpen={isErrorToastOpen}
        onClose={() => setIsErrorToastOpen(false)}
        title={t('benchmarks.error.title')}
        description={t('benchmarks.error.caption')}
        className="z-50 w-[333px] max-w-[333px]"
      />
    </>
  );
}

TrackTable.displayName = 'TrackTable';
